// Home.js
import React from 'react';
import { FaGlobeAfrica, FaSignInAlt, FaSearch, FaPlus } from 'react-icons/fa';
import '../styles/Home.css'
import algerieImage from "../assets/Algerie.png";

const Home = () => {
  return (
    <>
      {/* Animated Background */}
      <div className="bg-animation"></div>
      <div className="glass-overlay"></div>
      
      {/* Particles */}
      <div className="particles" id="particles"></div>

      {/* Navigation */}
      <nav id="navbar">
        <div className="logo">
          <FaGlobeAfrica /> DzWordNet
        </div>
        <div className="nav-logo-container">
          <img src="/src/assets/logo.png" alt="DzWordNet Logo" className="nav-logo" />
        </div>
        <a href="/login" className="btn-login">
          <FaSignInAlt /> Connexion
        </a>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span>🇩🇿 Patrimoine Linguistique Algérien</span>
          </div>
          <h1>Explorez la richesse du dialecte algérien</h1>
          <p className="hero-subtitle">
            Rejoignez notre communauté passionnée et contribuez à préserver l'âme linguistique de l'Algérie. 
            Découvrez, partagez et enrichissez notre dictionnaire collaboratif du <strong>Darija</strong>.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">19K+</span>
              <span className="stat-label">Mots répertoriés</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">3.5K+</span>
              <span className="stat-label">Contributeurs</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">69</span>
              <span className="stat-label">Wilayas représentées</span>
            </div>
          </div>
          <div className="cta-buttons">
            <a href="/dictionary" className="btn-primary">
              <FaSearch /> Explorer le dictionnaire
            </a>
            <a href="/dictionary/add" className="btn-secondary">
              <FaPlus /> Ajouter un mot
            </a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-card">
            <div className="card-header">
              <div className="card-icon">📚</div>
              <h3>Mot du jour</h3>
            </div>
            <div className="word-showcase">
              <div className="arabic-word">برك</div>
              <div className="phonetic">/bark/</div>
              <div className="definition">
                <strong>Assez !</strong> - Expression courante pour dire "ça suffit" ou "arrête"
              </div>
              <div className="example">
                <em>"برك من اللعب، روح اقرى"</em>
                <span>Arrête de jouer, va étudier</span>
              </div>
            </div>
            <div className="card-footer">
              <span className="region-tag">🏛️ Alger</span>
              <span className="category-tag">Expression courante</span>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-box">
          <div className="welcome-icon">🌟</div>
          <h2>Bienvenue dans l'univers du dialecte algérien</h2>
          <p>
            <span className="highlight">DzWordNet</span> est bien plus qu'un simple dictionnaire - c'est un pont entre les générations, 
            une célébration de notre identité culturelle et un outil de préservation de notre patrimoine linguistique unique.
            <br /><br />
            Chaque mot, chaque expression que vous partagez contribue à construire une mémoire collective vivante 
            de la <strong>langue du cœur algérien</strong>.
          </p>
          <div className="welcome-features">
            <div className="mini-feature">
              <span className="mini-icon">🎯</span>
              <span>Précision régionale</span>
            </div>
            <div className="mini-feature">
              <span className="mini-icon">🤝</span>
              <span>Collaboration communautaire</span>
            </div>
            <div className="mini-feature">
              <span className="mini-icon">📱</span>
              <span>Accessible partout</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-section">
        <div className="section-header">
          <h2 className="section-title">Découvrez nos fonctionnalités</h2>
          <p className="section-subtitle">Des outils puissants pour explorer et enrichir le dialecte algérien</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <FaSearch />
            </div>
            <div className="feature-badge">Populaire</div>
            <h3>Recherche intelligente</h3>
            <p>Explorez notre base de données enrichie avec une recherche avancée par région, catégorie, et contexte d'usage.</p>
            <div className="feature-highlights">
              <span>✨ Recherche phonétique</span>
              <span>🗺️ Filtrage par wilaya</span>
              <span>📊 Suggestions intelligentes</span>
            </div>
            <a href="/dictionary" className="feature-link">Commencer la recherche →</a>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <FaPlus />
            </div>
            <div className="feature-badge new">Nouveau</div>
            <h3>Contribution enrichie</h3>
            <p>Participez activement à la construction du dictionnaire avec nos outils de contribution intuitifs et collaboratifs.</p>
            <div className="feature-highlights">
              <span>🎙️ Enregistrement audio</span>
              <span>📝 Exemples contextuels</span>
              <span>⭐ Système de validation</span>
            </div>
            <a href="/dictionary/add" className="feature-link">Ajouter du contenu →</a>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <FaGlobeAfrica />
            </div>
            <div className="feature-badge">Communauté</div>
            <h3>Réseau culturel</h3>
            <p>Connectez-vous avec des passionnés de la langue algérienne et participez à des projets collaboratifs enrichissants.</p>
            <div className="feature-highlights">
              <span>👥 Forums de discussion</span>
              <span>📚 Projets collaboratifs</span>
              <span>🏆 Système de reconnaissance</span>
            </div>
            <a href="/projects" className="feature-link">Rejoindre la communauté →</a>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="quote-container">
          <div className="quote-mark">"</div>
          <p className="quote">
            اللغة هي هوية الشعب، والدارجة هي روح الجزائر<br />
            <em>La langue est l'identité du peuple, et le dialecte est l'âme de l'Algérie</em>
          </p>
          <div className="quote-author">
            — Sagesse populaire algérienne
          </div>
        </div>
      </section>

      {/* Algeria Map Section */}
      <section className="algeria-section">
        <div className="algeria-content">
          <div className="algeria-text">
            <h3>🇩🇿 D'Oran à Constantine, de Tamanrasset à Annaba</h3>
            <p>
              Chaque région d'Algérie apporte sa richesse linguistique unique à notre dialecte. 
              Explorez les variations régionales et découvrez la diversité de notre patrimoine oral.
            </p>
          </div>
          <div className="algeria-map-container">
            <img src={algerieImage} alt="Carte Algérie" />
            {/* <div className="map-dots">
              <div className="map-dot" style={{top: '30%', left: '20%'}} data-region="Oran"></div>
              <div className="map-dot" style={{top: '25%', left: '45%'}} data-region="Alger"></div>
              <div className="map-dot" style={{top: '20%', left: '70%'}} data-region="Constantine"></div>
              <div className="map-dot" style={{top: '70%', left: '35%'}} data-region="Tamanrasset"></div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <FaGlobeAfrica /> DzWordNet
              </div>
              <p>Préservons ensemble le patrimoine linguistique algérien pour les générations futures.</p>
            </div>
            <div className="footer-links">
              <div className="footer-section">
                <h4>Explorer</h4>
                <a href="/dictionary">Dictionnaire</a>
                <a href="/regions">Par région</a>
                <a href="/categories">Catégories</a>
              </div>
              <div className="footer-section">
                <h4>Contribuer</h4>
                <a href="/dictionary/add">Ajouter un mot</a>
                <a href="/projects">Projets</a>
                <a href="/community">Communauté</a>
              </div>
            </div>
          </div>
          <div className="social-links">
            <a href="#" className="social-link" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
            <a href="#" className="social-link" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
            <a href="#" className="social-link" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
            <a href="#" className="social-link" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
          </div>
          <div className="footer-bottom">
            <p className="copyright">
              © 2026 DzWordNet. Fait avec ❤️ pour préserver notre héritage linguistique.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Home;